import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import {
  FiArrowRight, FiUsers, FiCheckCircle, FiXCircle, FiClock,
  FiInstagram, FiMessageSquare, FiExternalLink
} from 'react-icons/fi';
import { FaTiktok } from 'react-icons/fa';
import { campaignsApi, applicationsApi } from '../../utils/api';
import { extractSocialUsername, buildSocialProfileUrl } from '../../utils/socialUsername';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';
import './CampaignApplicationsPage.css';

const CampaignApplicationsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedApplication, setSelectedApplication] = useState(null);

  const { data: campaignData } = useQuery(
    ['campaign', id],
    () => campaignsApi.getById(id)
  );

  const { data: applicationsData, isLoading } = useQuery(
    ['campaign-applications', id, statusFilter],
    () => applicationsApi.getCampaign(id, { status: statusFilter, limit: 50 })
  );

  const updateStatusMutation = useMutation(
    ({ applicationId, status, rejectionReason }) =>
      applicationsApi.updateStatus(applicationId, { status, rejectionReason }),
    {
      onSuccess: () => {
        toast.success('הסטטוס עודכן בהצלחה');
        queryClient.invalidateQueries(['campaign-applications', id]);
        setSelectedApplication(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'שגיאה בעדכון הסטטוס');
      }
    }
  );

  const campaign = campaignData?.data?.campaign;
  const applications = applicationsData?.data?.applications || [];

  const handleApprove = (applicationId) => {
    updateStatusMutation.mutate({ applicationId, status: 'approved' });
  };

  const handleReject = (applicationId, reason) => {
    updateStatusMutation.mutate({
      applicationId,
      status: 'rejected',
      rejectionReason: reason || 'הפרופיל אינו מתאים לקמפיין'
    });
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'pending':
        return { label: 'ממתין', icon: FiClock, className: 'status-pending' };
      case 'approved':
        return { label: 'אושר', icon: FiCheckCircle, className: 'status-approved' };
      case 'rejected':
        return { label: 'נדחה', icon: FiXCircle, className: 'status-rejected' };
      default:
        return { label: status, icon: FiClock, className: '' };
    }
  };

  return (
    <div className="campaign-applications-page">
      <button className="back-btn" onClick={() => navigate('/dashboard/campaigns')}>
        <FiArrowRight />
        חזרה לקמפיינים
      </button>

      {/* Campaign Header */}
      <div className="campaign-header-card">
        <div className="campaign-info">
          <h1>{campaign?.title || 'קמפיין'}</h1>
          <div className="campaign-stats">
            <span><FiUsers /> {applications.length} הגשות</span>
            <span className={`badge ${campaign?.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
              {campaign?.status === 'active' ? 'פעיל' : campaign?.status}
            </span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <button
          className={`filter-btn ${statusFilter === '' ? 'active' : ''}`}
          onClick={() => setStatusFilter('')}
        >
          הכל ({applications.length})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
          onClick={() => setStatusFilter('pending')}
        >
          ממתינים ({applications.filter(a => a.status === 'pending').length})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
          onClick={() => setStatusFilter('approved')}
        >
          אושרו ({applications.filter(a => a.status === 'approved').length})
        </button>
        <button
          className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => setStatusFilter('rejected')}
        >
          נדחו ({applications.filter(a => a.status === 'rejected').length})
        </button>
      </div>

      {/* Applications List */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner"></div>
        </div>
      ) : applications.length === 0 ? (
        <div className="empty-state">
          <FiUsers className="empty-icon" />
          <h3>אין הגשות להצגה</h3>
          <p>עדיין לא התקבלו הגשות לקמפיין זה</p>
        </div>
      ) : (
        <div className="applications-grid">
          {applications.map((application, index) => {
            const statusInfo = getStatusInfo(application.status);
            const user = application.userId;
            const totalFollowers = application.creatorSnapshot?.followers || 0;

            return (
              <motion.div
                key={application._id}
                className="application-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                {/* User Header */}
                <div className="user-header">
                  <div className="user-avatar">
                    {user?.profileImage ? (
                      <img src={user.profileImage} alt="" />
                    ) : (
                      <span>{user?.firstName?.charAt(0)}</span>
                    )}
                  </div>
                  <div className="user-info">
                    <h3>{user?.firstName} {user?.lastName}</h3>
                    <span className="followers-count">
                      <FiUsers />
                      {totalFollowers.toLocaleString()} עוקבים
                    </span>
                  </div>
                  <span className={`status-badge ${statusInfo.className}`}>
                    <statusInfo.icon />
                    {statusInfo.label}
                  </span>
                </div>

                {/* Social Links */}
                <div className="social-links">
                  {(() => {
                    const ig = extractSocialUsername('instagram', user?.socialMedia?.instagram?.username);
                    return ig ? (
                      <a
                        href={buildSocialProfileUrl('instagram', ig)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link instagram"
                      >
                        <FiInstagram />
                        @{ig}
                      </a>
                    ) : null;
                  })()}
                  {(() => {
                    const tt = extractSocialUsername('tiktok', user?.socialMedia?.tiktok?.username);
                    return tt ? (
                      <a
                        href={buildSocialProfileUrl('tiktok', tt)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link tiktok"
                      >
                        <FaTiktok />
                        @{tt}
                      </a>
                    ) : null;
                  })()}
                </div>

                {/* Categories */}
                {user?.categories?.length > 0 && (
                  <div className="categories">
                    {user.categories.slice(0, 4).map((cat, i) => (
                      <span key={i} className="category-badge">{cat}</span>
                    ))}
                  </div>
                )}

                {/* Message */}
                {application.message && (
                  <div className="application-message">
                    <FiMessageSquare />
                    <p>"{application.message}"</p>
                  </div>
                )}

                {/* Meta */}
                <div className="application-meta">
                  <span>
                    הוגש: {format(new Date(application.createdAt), 'd MMM yyyy', { locale: he })}
                  </span>
                </div>

                {/* Actions */}
                {application.status === 'pending' && (
                  <div className="application-actions">
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleApprove(application._id)}
                      disabled={updateStatusMutation.isLoading}
                    >
                      <FiCheckCircle />
                      אשר
                    </button>
                    <button
                      className="btn btn-error btn-sm"
                      onClick={() => handleReject(application._id)}
                      disabled={updateStatusMutation.isLoading}
                    >
                      <FiXCircle />
                      דחה
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignApplicationsPage;
